import React from "react";
import { Table, Badge, Button, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem, Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label } from "reactstrap";
import Swal from "sweetalert2";
import Select from "react-select";
import { archiveRoom, updateRoomMetadata, getAllModerators, assignModerator, removeModerator } from "api/ApiServices";

const AVATAR_GRADIENTS = [
    "linear-gradient(135deg, #5e72e4, #825ee4)",
    "linear-gradient(135deg, #2dce89, #2dcecc)",
    "linear-gradient(135deg, #fb6340, #fbb140)",
    "linear-gradient(135deg, #11cdef, #1171ef)",
    "linear-gradient(135deg, #f5365c, #f56036)",
    "linear-gradient(135deg, #172b4d, #5e72e4)",
];

const modalSelectStyles = {
    control: (provided) => ({
        ...provided,
        borderRadius: "8px",
        minHeight: "45px",
        border: "1px solid #dee2e6",
        boxShadow: "none",
        "&:hover": { borderColor: "#5e72e4" },
        fontSize: "14px",
        background: "#fff",
        color: "#525f7f"
    }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected ? "#5e72e4" : state.isFocused ? "#f8f9fe" : "#fff",
        color: state.isSelected ? "#fff" : "#525f7f",
        fontSize: "14px",
        padding: "10px 15px",
        cursor: "pointer"
    }),
    menu: (provided) => ({
        ...provided,
        borderRadius: "12px",
        boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
        border: "none",
        marginTop: "8px",
        overflow: "hidden",
        zIndex: 9999
    })
};

const AssignedRoomsTable = ({ data, onViewActivity, onRefresh }) => {
    // Determine if the user is a moderator
    const role = localStorage.getItem("userRole");
    const roleLower = role?.toLowerCase() || "";
    const isAdmin = roleLower === "admin" || roleLower === "super admin" || roleLower === "super_admin";
    const isMod = !isAdmin;
    console.log("[AssignedRoomsTable] role =", role, "| roleLower =", roleLower, "| isAdmin =", isAdmin, "| isMod =", isMod);

    const [moderatorModalOpen, setModeratorModalOpen] = React.useState(false);
    const [selectedRoomId, setSelectedRoomId] = React.useState(null);
    const [selectedModerator, setSelectedModerator] = React.useState("");
    const [allAdmins, setAllAdmins] = React.useState([]);

    React.useEffect(() => {
        if (moderatorModalOpen) {
            getAllModerators().then(res => {
                if (res.status === true || res.status === "success") {
                    setAllAdmins(res.payload || []);
                }
            });
        }
    }, [moderatorModalOpen]);

    const handleModeratorSubmit = async (e) => {
        e.preventDefault();

        if (!selectedModerator) return;

        try {
            const res = await assignModerator(selectedRoomId, { userId: selectedModerator });

            if (res.status === true || res.status === "success") {
                Swal.fire({
                    title: "Success",
                    text: "Moderator assigned successfully",
                    icon: "success",
                    showConfirmButton: false,
                    timer: 2000
                });

                setModeratorModalOpen(false);
                setSelectedModerator("");
                if (onRefresh) onRefresh();

            } else {
                Swal.fire({
                    title: "Error",
                    text: res.message || "Assignment failed",
                    icon: "error",
                    showConfirmButton: false,
                    timer: 2000
                });
            }

        } catch (err) {
            Swal.fire({
                title: "Error",
                text: "Assignment failed.",
                icon: "error",
                showConfirmButton: false,
                timer: 2000
            });
        }
    };

    const handleRemoveModerator = async (roomId, modIdentifier) => {
        const result = await Swal.fire({
            title: "Remove Moderator?",
            text: "Are you sure you want to remove this moderator from the room?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, Remove",
            cancelButtonText: "Cancel",
            confirmButtonColor: "#f5365c",
            cancelButtonColor: "#8898aa"
        });

        if (result.isConfirmed) {
            try {
                const res = await removeModerator(roomId, { userId: modIdentifier });
                if (res.status === true || res.status === "success") {
                    Swal.fire({
                        title: "Success",
                        text: "Moderator removed successfully",
                        icon: "success",
                        timer: 2000,
                        showConfirmButton: false
                    });
                    if (onRefresh) onRefresh();
                } else {
                    Swal.fire("Error", res.message || "Failed to remove moderator", "error");
                }
            } catch (err) {
                console.error("Error removing moderator:", err);
                Swal.fire("Error", "Failed to remove moderator", "error");
            }
        }
    };

    const handleVisibilityChange = async (room) => {
        const currentVisibility = room.r_visibility || room.visibility || "Public";
        const result = await Swal.fire({
            title: "Manage Visibility",
            text: `Select visibility for room: ${room.name || room.r_name || "Chat Room"}`,
            input: "select",
            inputOptions: {
                "Public": "Public",
                "Private": "Private",
                "Hidden": "Hidden"
            },
            inputValue: currentVisibility,
            showCancelButton: true,
            confirmButtonText: "Update Visibility",
            cancelButtonText: "Cancel",
            confirmButtonColor: "#5e72e4",
            cancelButtonColor: "#8898aa"
        });

        if (result.isConfirmed && result.value) {
            try {
                const newVisibility = result.value;
                const roomId = room.id || room._id;
                const payload = {
                    r_visibility: newVisibility,
                    visibility: newVisibility
                };
                const res = await updateRoomMetadata(roomId, payload);
                if (res.status === true || res.status === "success") {
                    Swal.fire({
                        title: "Success",
                        text: `Visibility updated to ${newVisibility} successfully`,
                        icon: "success",
                        timer: 2000,
                        showConfirmButton: false
                    });
                    if (onRefresh) onRefresh();
                } else {
                    Swal.fire("Error", res.message || "Failed to update visibility", "error");
                }
            } catch (err) {
                console.error("Error updating visibility:", err);
                Swal.fire("Error", "Failed to update visibility", "error");
            }
        }
    };

    const handleArchiveRoom = async (room) => {
        const result = await Swal.fire({
            title: "Archive Room?",
            text: `Are you sure you want to archive room: ${room.name || room.r_name || "Chat Room"}?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, Archive",
            cancelButtonText: "Cancel",
            confirmButtonColor: "#f5365c",
            cancelButtonColor: "#8898aa"
        });

        if (result.isConfirmed) {
            try {
                const roomId = room.id || room._id;
                const res = await archiveRoom(roomId, "Archived");
                if (res.status === true || res.status === "success") {
                    Swal.fire({
                        title: "Success",
                        text: "Room archived successfully",
                        icon: "success",
                        timer: 2000,
                        showConfirmButton: false
                    });
                    if (onRefresh) onRefresh();
                } else {
                    Swal.fire("Error", res.message || "Failed to archive room", "error");
                }
            } catch (err) {
                console.error("Error archiving room:", err);
                Swal.fire("Error", "Failed to archive room", "error");
            }
        }
    };

    const handleRestoreRoom = async (room) => {
        const result = await Swal.fire({
            title: "Restore Room?",
            text: `Are you sure you want to restore room: ${room.name || room.r_name || "Chat Room"}?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, Restore",
            cancelButtonText: "Cancel",
            confirmButtonColor: "#2dce89",
            cancelButtonColor: "#8898aa"
        });

        if (result.isConfirmed) {
            try {
                const roomId = room.id || room._id;
                const res = await archiveRoom(roomId, "Active");
                if (res.status === true || res.status === "success") {
                    Swal.fire({
                        title: "Success",
                        text: "Room restored successfully",
                        icon: "success",
                        timer: 2000,
                        showConfirmButton: false
                    });
                    if (onRefresh) onRefresh();
                } else {
                    Swal.fire("Error", res.message || "Failed to restore room", "error");
                }
            } catch (err) {
                console.error("Error restoring room:", err);
                Swal.fire("Error", "Failed to restore room", "error");
            }
        }
    };

    return (
        <>
        <Table className="align-items-center moderation-table table-flush" responsive hover>
            <thead className="thead-light">
                <tr>
                    <th scope="col" style={{ padding: "16px 24px" }}>Room Name</th>
                    <th scope="col">Type</th>
                    <th scope="col">Location</th>
                    {!isMod && <th scope="col">Assigned Moderator(s)</th>}
                    <th scope="col" className="text-center">Users / Messages</th>
                    <th scope="col">Status</th>
                    <th scope="col" className="text-right" style={{ paddingRight: "24px" }}>Actions</th>
                </tr>
            </thead>
            <tbody>
                {data.map((room, idx) => {
                    const roomName = room.name || room.r_name || "Chat Room";
                    const roomType = room.type || "Room";
                    const location = room.city && room.city !== "N/A" ? `${room.city}, ${room.country}` : room.country || "Global";
                    
                    // Fallback avatar/image logic similar to Rooms.js
                    const rawImage = room.r_image || room.image;
                    let imageUrl = null;
                    if (rawImage) {
                        if (rawImage.startsWith("http")) {
                            imageUrl = rawImage;
                        } else {
                            const cleanImage = rawImage.replace(/^\/+/, "");
                            imageUrl = `https://cradleaibucket.s3.us-east-1.amazonaws.com/communities/${cleanImage.split("/").pop()}`;
                        }
                    }

                    // Assigned moderators
                    const moderators = room.moderatorsInfo || [];

                    return (
                        <tr key={room.id || room._id} style={{ transition: "all 0.15s ease" }}>
                            <td style={{ padding: "16px 24px" }}>
                                <div className="d-flex align-items-center">
                                    <div
                                        style={{
                                            width: "38px",
                                            height: "38px",
                                            borderRadius: "10px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            fontWeight: 700,
                                            fontSize: "15px",
                                            color: "#fff",
                                            flexShrink: 0,
                                            boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
                                            background: AVATAR_GRADIENTS[idx % AVATAR_GRADIENTS.length],
                                            overflow: "hidden"
                                        }}
                                        className="mr-3"
                                    >
                                        {imageUrl ? (
                                            <img
                                                src={imageUrl}
                                                alt={roomName}
                                                style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }}
                                                onError={(e) => {
                                                    e.currentTarget.onerror = null;
                                                    e.currentTarget.src = "https://via.placeholder.com/100x100.png?text=Room";
                                                }}
                                            />
                                        ) : (
                                            <span>{roomName.charAt(0).toUpperCase()}</span>
                                        )}
                                    </div>
                                    <div>
                                        <span className="font-weight-600 text-dark text-sm">{roomName}</span>
                                    </div>
                                </div>
                            </td>
                            <td>
                                <Badge color="" style={{ background: "#f1eaff", color: "#825ee4", border: "none", fontSize: "10px" }} className="text-uppercase">
                                    {roomType}
                                </Badge>
                            </td>
                            <td>
                                <span className="text-sm font-weight-500 text-dark">{location}</span>
                            </td>
                            {!isMod && (
                                <td>
                                    {moderators.length === 0 ? (
                                        <span className="text-xs text-warning font-weight-bold">
                                            <i className="fas fa-exclamation-triangle mr-1" /> No Moderator
                                        </span>
                                    ) : (
                                        <div className="d-flex flex-column" style={{ gap: "4px" }}>
                                            {moderators.map((mod, index) => {
                                                const modName = `${mod.au_name} ${mod.au_surname || ""}`.trim();
                                                return (
                                                    <div key={index} className="d-flex align-items-center">
                                                        <div 
                                                            style={{ 
                                                                width: "22px", 
                                                                height: "22px", 
                                                                borderRadius: "50%", 
                                                                background: "#11cdef", 
                                                                color: "#fff", 
                                                                display: "flex", 
                                                                alignItems: "center", 
                                                                justifyContent: "center", 
                                                                fontSize: "10px", 
                                                                fontWeight: "bold",
                                                                overflow: "hidden"
                                                            }} 
                                                            className="mr-2"
                                                        >
                                                            {mod.au_image ? (
                                                                <img src={mod.au_image.startsWith("http") ? mod.au_image : `${process.env.REACT_APP_IMAGE_PATH || ""}${mod.au_image}`} alt={modName} onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(modName)}&background=11cdef&color=fff`; }} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                                            ) : (
                                                                <span>{modName.charAt(0).toUpperCase()}</span>
                                                            )}
                                                        </div>
                                                        <div>
                                                            <span className="text-xs font-weight-600 text-dark d-block" style={{ lineHeight: "1.2" }}>{modName}</span>
                                                            <small className="text-muted" style={{ fontSize: "9px" }}>{mod.au_email}</small>
                                                        </div>
                                                        {false && (
                                                            <i 
                                                                className="fas fa-times-circle text-danger ml-2" 
                                                                style={{ cursor: "pointer", fontSize: "12px" }}
                                                                title="Remove Moderator"
                                                                onClick={() => handleRemoveModerator(room.id || room._id, mod.au_uuid || mod.au_email)}
                                                            />
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </td>
                            )}
                            <td className="text-center">
                                <div className="d-flex justify-content-center align-items-center" style={{ gap: "12px" }}>
                                    <div>
                                        <small className="text-muted d-block" style={{ fontSize: "10px" }}>USERS</small>
                                        <span className="text-sm font-weight-600 text-dark">{room.users || 0}</span>
                                    </div>
                                    <div style={{ height: "20px", width: "1px", background: "#e9ecef" }} />
                                    <div>
                                        <small className="text-muted d-block" style={{ fontSize: "10px" }}>MESSAGES</small>
                                        <span className="text-sm font-weight-600 text-dark">{room.messages || 0}</span>
                                    </div>
                                </div>
                            </td>
                            <td>
                                <div className="d-flex align-items-center">
                                    <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: room.status === "Active" ? "#2dce89" : "#adb5bd", marginRight: "8px" }} />
                                    <span style={{ fontSize: "13px", color: room.status === "Active" ? "#2dce89" : "#8898aa" }} className="font-weight-500">{room.status}</span>
                                </div>
                            </td>
                            <td className="text-right" style={{ paddingRight: "24px" }}>
                                <div className="d-flex justify-content-end align-items-center" style={{ gap: "8px" }}>
                                    <Button
                                        color="primary"
                                        size="sm"
                                        style={{ background: "linear-gradient(135deg, #5e72e4, #825ee4)", border: "none", borderRadius: "8px", fontWeight: "bold" }}
                                        onClick={() => onViewActivity && onViewActivity(room)}
                                    >
                                        <i className="fas fa-eye mr-1" /> View Activity
                                    </Button>
                                    {isAdmin && (
                                        <div style={{ width: "32px", display: "flex", justifyContent: "center", visibility: moderators.length > 0 ? "visible" : "hidden" }}>
                                            <UncontrolledDropdown>
                                                <DropdownToggle size="sm" className="btn-icon-only" style={{ background: "transparent", border: "none" }}>
                                                    <i className="fas fa-ellipsis-v" style={{ color: "#6c757d" }} />
                                                </DropdownToggle>
                                                <DropdownMenu right className="shadow-lg border-0" style={{ borderRadius: "12px" }}>
                                                    <DropdownItem onClick={() => handleRemoveModerator(room.id || room._id, moderators[0]?.au_uuid || moderators[0]?.au_email)} className="text-danger">
                                                        <i className="fas fa-user-times mr-2" /> Remove Moderator
                                                    </DropdownItem>
                                                </DropdownMenu>
                                            </UncontrolledDropdown>
                                        </div>
                                    )}
                                </div>
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </Table>

        {/* Moderator Modal */}
        <Modal isOpen={moderatorModalOpen} toggle={() => setModeratorModalOpen(!moderatorModalOpen)} centered>
            <ModalHeader toggle={() => setModeratorModalOpen(false)}>Assign Moderator</ModalHeader>
            <Form onSubmit={handleModeratorSubmit}>
                <ModalBody>
                    <FormGroup>
                        <Label>Select Moderator</Label>
                        <Select
                            value={allAdmins.find(a => String(a._id) === selectedModerator) ? { value: selectedModerator, label: `${allAdmins.find(a => String(a._id) === selectedModerator).au_name || "N/A"} ${allAdmins.find(a => String(a._id) === selectedModerator).au_surname || ""} (${allAdmins.find(a => String(a._id) === selectedModerator).au_email || ""})` } : null}
                            onChange={(opt) => setSelectedModerator(opt ? opt.value : "")}
                            options={allAdmins.map(a => ({ value: String(a._id), label: `${a.au_name || "N/A"} ${a.au_surname || ""} (${a.au_email || ""})` }))}
                            placeholder="Search by name or email..."
                            isSearchable={true}
                            styles={modalSelectStyles}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            isClearable={true}
                        />
                    </FormGroup>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => setModeratorModalOpen(false)}>Cancel</Button>
                    <Button color="success" type="submit" disabled={!selectedModerator}>Assign</Button>
                </ModalFooter>
            </Form>
        </Modal>
        </>
    );
};

export default AssignedRoomsTable;
